import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { BrowserModule } from '@angular/platform-browser';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, HTTP_INTERCEPTORS, withFetch } from '@angular/common/http';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalInterceptor, MSAL_INSTANCE, MsalInterceptorConfiguration, MsalGuardConfiguration, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalService, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { provideStore, MetaReducer} from "@ngrx/store";
import { reducers } from "./app-store";
import { CurrentUserActions } from "./app-store/current-user/current-user.actions";
import { ServiceWorkerModule } from "@angular/service-worker";
import { ToastrModule } from 'ngx-toastr';
import { forEach } from 'lodash';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { sessionInterceptorFn } from './custom.interceptor';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export const metaReducers: MetaReducer<any, CurrentUserActions>[] = [];

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri,
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: environment.msalConfig.auth.cacheLocation,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        logLevel: LogLevel.Verbose,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        },
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const resourceMap = new Map<string, Array<string> | null>();

  //Dont protect (must be before you protect a url.)
  environment.apiConfig.unprotectedResources.forEach(url => {
    resourceMap.set(url, null);
  });

  //protect all
  const restrictAll = environment.apiBaseUri + "*";
  resourceMap.set(restrictAll, [...environment.apiConfig.scopes]);
      
  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap: resourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: [...environment.apiConfig.consentScopes],
    },
    loginFailedRoute: '/login-failed'
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),    
    importProvidersFrom(BrowserModule, MatButtonModule, MatToolbarModule, MatListModule, MatMenuModule, ToastrModule.forRoot()),
    provideNoopAnimations(),
    provideHttpClient(
      withInterceptors([
        sessionInterceptorFn,
      ]),
      withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,    
    provideStore(reducers, { metaReducers }),
    {
      provide: ServiceWorkerModule,
      useFactory: () => ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    }, provideAnimationsAsync()
  ]
};
